import React, { createContext, useContext, useMemo } from 'react';
import { useSubscription } from 'shared/hooks';

export type VisualRenderIdentity = {
    reportId: string;
    pageId?: number | null;
    id: string | number;
    type: string;
    revision?: string;
};

type RenderObservation = VisualRenderIdentity & { message: string; observedAt: string; severity: 'error' | 'warning' };
type ObserveRender = (message: string, severity: RenderObservation['severity']) => () => void;
const RenderFeedbackContext = createContext<ObserveRender | null>(null);
// Mounted observations only: no persistence, polling, or independent repair loop.
const observations = new Map<symbol, RenderObservation>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach(listener => listener());

export const VisualRenderFeedback: React.FC<{
    visual?: VisualRenderIdentity;
    children: React.ReactNode;
}> = ({ visual, children }) => {
    const { reportId, pageId, id, type, revision } = visual ?? {};
    const observe = useMemo<ObserveRender | null>(() => {
        if (!reportId || id == null || !type) return null;
        return (message, severity) => {
            const token = Symbol();
            observations.set(token, { reportId, pageId, id, type, revision, severity,
                message: message.slice(0, 1000), observedAt: new Date().toISOString() });
            changed();
            return () => { observations.delete(token); changed(); };
        };
    }, [reportId, pageId, id, type, revision]);
    return <RenderFeedbackContext.Provider value={observe}>{children}</RenderFeedbackContext.Provider>;
};

export const useVisualRenderError = (message?: string) => {
    const observe = useContext(RenderFeedbackContext);
    useSubscription(() => message ? observe?.(message, 'error') : undefined, [message, observe]);
};

export const useVisualRenderWarning = (message?: string) => {
    const observe = useContext(RenderFeedbackContext);
    useSubscription(() => message ? observe?.(message, 'warning') : undefined, [message, observe]);
};

/** The render errors of a report's mounted visuals now; ``onRenderObservations`` hears every change (PreviewPage's native host). */
export const renderErrors = (reportId: string) =>
    Array.from(observations.values()).filter(item => item.reportId === reportId && item.severity === 'error');
export const onRenderObservations = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/** Capture at send time so a recovered/unmounted visual cannot leave stale errors in chat. */
export const withVisualRenderFeedback = (layout: Record<string, any> | null | undefined, reportId: string) => ({
    ...layout,
    reportId,
    renderErrors: renderErrors(reportId).slice(0, 20),
    renderWarnings: Array.from(observations.values()).filter(item => item.reportId === reportId && item.severity === 'warning').slice(0, 20),
});
